import { useEffect, useRef } from 'react';
import { useUI, type Mode, type BottomPanelState } from '@/state/store';
import { useLoader } from '../store';
import type { SceneMode } from '../globe/GlobeEngine';

type Context = {
  sceneMode: SceneMode;
  coverageEnabled: boolean;
  coverageRingsEnabled: boolean;
  bottomTab: string;
  bottomPanel: BottomPanelState;
};
export function defaultModeContext(mode: Mode, network: string): Context {
  return {
    sceneMode: mode === 'operations' || (mode === 'loader' && (network === 'artemis' || network === 'mars' || network === 'full_catalogue')) ? '3D' : '2D',
    coverageEnabled: mode === 'analysis',
    coverageRingsEnabled: mode === 'analysis',
    bottomTab: mode === 'operations' ? 'aoslos' : mode === 'analysis' ? 'matrix' : 'gantt',
    bottomPanel: mode === 'analysis' ? 'default' : 'collapsed',
  };
}

/** Remember each mode's working view without resetting selection or time. */
export function useModeContext(mode: Mode, network: string, parked: boolean) {
  const saved = useRef(new Map<string, Context>());
  const previous = useRef<string | null>(null);
  useEffect(() => {
    if (parked || !['operations', 'analysis', 'loader'].includes(mode)) return;
    const key = `${network}:${mode}`;
    const loader = useLoader.getState();
    const ui = useUI.getState();
    const next = saved.current.get(key) ?? defaultModeContext(mode, network);
    const changed = key !== previous.current;
    previous.current = key;
    if (changed && !ui.presenter) {
      loader.setView({
        sceneMode: next.sceneMode,
        coverageEnabled: next.coverageEnabled,
        coverageRingsEnabled: next.coverageRingsEnabled,
      });
      useUI.setState({ bottomTab: next.bottomTab, bottomPanel: next.bottomPanel });
    }
    // Capture while this context is active. setNetwork resets the globe view
    // before React renders; capturing only on departure loses the old view.
    const remember = () => {
      const l = useLoader.getState();
      const u = useUI.getState();
      if (l.network !== network || u.presenter) return;
      saved.current.set(key, {
        sceneMode: l.sceneMode,
        coverageEnabled: l.coverageEnabled,
        coverageRingsEnabled: l.coverageRingsEnabled,
        bottomTab: u.bottomTab,
        bottomPanel: u.bottomPanel,
      });
    };
    remember();
    const stopLoader = useLoader.subscribe(remember);
    const stopUI = useUI.subscribe(remember);
    return () => {
      stopLoader();
      stopUI();
    };
  }, [mode, network, parked]);
}
